import { getRelatedRoomPackageId } from '../PackageSetupStep/roomMapping';

import { getStringValue } from './bedding';
import type { SalesforceRoomSource } from './bedding';

export function getRoomAssetId(room: SalesforceRoomSource) {
  return getStringValue(room.room_asset?.sfid) ?? getStringValue(room.room_asset?.id);
}

// One draft per Salesforce room asset, so its id is stable and shared by every deal on that asset.
export function getRoomAssetDraftId(room: SalesforceRoomSource) {
  const roomAssetId = getRoomAssetId(room);

  return roomAssetId ? `asset:${roomAssetId}` : undefined;
}

/** Whether an asset's room is the AI's untouched suggestion or the CM's own pick. */
export type RoomSource = 'ai' | 'manual';

export interface RoomAssetRow {
  assetId: string;
  assetName: string;
  /** Names of the deal options that use this asset, so a CM can tell same-named assets apart. */
  dealOptionNames: Array<string>;
}

/** One row per Salesforce room asset, however many deal options share it. */
export function getRoomAssetRows(rooms: Array<SalesforceRoomSource> = []): Array<RoomAssetRow> {
  const rowsByAssetId = new Map<string, RoomAssetRow>();

  rooms.forEach((room) => {
    const assetId = getRoomAssetId(room);
    const assetName = getStringValue(room.room_asset?.name);

    if (assetId && assetName) {
      rowsByAssetId.set(assetId, {
        assetId,
        assetName,
        dealOptionNames: (rowsByAssetId.get(assetId)?.dealOptionNames ?? []).concat(
          getStringValue(room.name) || 'Unnamed deal option',
        ),
      });
    }
  });

  return Array.from(rowsByAssetId.values()).sort((rowA, rowB) =>
    rowA.assetName.localeCompare(rowB.assetName, undefined, { numeric: true }),
  );
}

export function getAssetIdByPackageId(rooms: Array<SalesforceRoomSource> = []) {
  const assetIdByPackageId: Record<string, string> = {};

  rooms.forEach((room) => {
    const packageId = getRelatedRoomPackageId(room);
    const assetId = getRoomAssetId(room);

    if (packageId && assetId) {
      assetIdByPackageId[packageId] = assetId;
    }
  });

  return assetIdByPackageId;
}

/**
 * The AI's suggested room per Salesforce room asset: the existing room type it matched, else the draft room
 * proposed for it (keyed by draftId, resolved to the real id at Submission).
 */
export function buildSuggestedAssetRoomKeys({
  existingRoomTypes,
  matchedRoomTypeNameBySalesforceName,
  rooms,
}: {
  existingRoomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
  matchedRoomTypeNameBySalesforceName: Record<string, string>;
  rooms: Array<SalesforceRoomSource>;
}) {
  const suggestions: Record<string, string> = {};

  rooms.forEach((room) => {
    const assetId = getRoomAssetId(room);
    const roomAssetName = room.room_asset?.name;
    const matchedRoomTypeName = roomAssetName ? matchedRoomTypeNameBySalesforceName[roomAssetName] : undefined;
    const matchedRoomType = matchedRoomTypeName
      ? existingRoomTypes.find((roomType) => roomType.name === matchedRoomTypeName)
      : undefined;
    const roomKey = matchedRoomType?.id ?? getRoomAssetDraftId(room);

    if (assetId && roomAssetName && roomKey) {
      suggestions[assetId] = roomKey;
    }
  });

  return suggestions;
}
